import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  PrimaryButton,
  FormField,
  FormSelect,
  ErrorBanner,
  Dialog,
  DialogBoddy,
  DialogFooter,
  DialogHeader,
} from '@/components';
import { useCountries } from '@/features/country/hooks/useCountries';
import { checkCompany, createJoinRequest } from '../api/company';

interface JoinCompanyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function JoinCompanyDialog({ isOpen, onClose, onSuccess }: JoinCompanyDialogProps) {
  const { t } = useTranslation();

  const [countryId, setCountryId] = useState('');
  const [companyIdentifier, setCompanyIdentifier] = useState('');
  const [searchResult, setSearchResult] = useState<{
    companyId: string;
    companyName: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: countries, isLoading: isCountriesLoading } = useCountries();

  const checkCompanyMutation = useMutation({
    mutationFn: checkCompany,
    onSuccess: (data) => {
      if (data.status === 'existing' && data.company_id && data.company_name) {
        setSearchResult({
          companyId: data.company_id,
          companyName: data.company_name,
        });
        setError(null);
      } else if (data.status === 'not_found') {
        setSearchResult(null);
        setError(t('companySelector.joinDialog.companyNotFound'));
      } else if (data.status === 'pending') {
        setSearchResult(null);
        setError(t('companySelector.joinDialog.companyPending'));
      } else {
        setSearchResult(null);
        setError(t('companySelector.joinDialog.companyNotFound'));
      }
    },
    onError: (error) => {
      setSearchResult(null);
      if (error.message?.includes('network')) {
        setError(t('companySelector.joinDialog.networkError'));
      } else {
        setError(t('companySelector.joinDialog.searchError'));
      }
    },
  });

  const joinRequestMutation = useMutation({
    mutationFn: createJoinRequest,
    onSuccess: () => {
      onSuccess?.();
      handleClose();
    },
    onError: (error) => {
      if (error.message?.includes('already exists')) {
        setError(t('companySelector.joinDialog.requestAlreadyExists'));
      } else if (error.message?.includes('network')) {
        setError(t('companySelector.joinDialog.networkError'));
      } else {
        setError(t('companySelector.joinDialog.joinError'));
      }
    },
  });

  const handleClose = useCallback(() => {
    setCountryId('');
    setCompanyIdentifier('');
    setSearchResult(null);
    setError(null);
    onClose();
  }, [onClose]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryId(e.target.value);
    setSearchResult(null);
    setError(null);
  };

  const handleCompanyIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyIdentifier(e.target.value);
    setSearchResult(null);
    setError(null);
  };

  const handleSearch = () => {
    if (!countryId || !companyIdentifier.trim()) {
      setError(t('companySelector.joinDialog.fillAllFields'));
      return;
    }
    setError(null);
    checkCompanyMutation.mutate({
      country_id: countryId,
      company_identifier: companyIdentifier.trim(),
    });
  };

  const handleJoin = () => {
    if (!searchResult) return;
    joinRequestMutation.mutate({
      companyUid: companyIdentifier.trim(),
      countryId: countryId,
    });
  };

  const isSearching = checkCompanyMutation.isPending;
  const isJoining = joinRequestMutation.isPending;

  return (
    <Dialog isOpen={isOpen} onClose={handleClose}>
      <DialogHeader>{t('companySelector.joinDialog.title')}</DialogHeader>
      <DialogBoddy>
        <p className="text-muted mb-4">{t('companySelector.joinDialog.description')}</p>

        {error && (
          <div className="mb-4">
            <ErrorBanner hasError={true}>{error}</ErrorBanner>
          </div>
        )}

        <div className="space-y-4">
          <FormSelect
            label={t('companySelector.joinDialog.country')}
            value={countryId}
            onChange={handleCountryChange}
            disabled={isCountriesLoading || isSearching || isJoining}
          >
            <option value="">{t('companyAdmin.form.countryPlaceholder')}</option>
            {countries?.countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </FormSelect>

          <FormField
            label={t('companySelector.joinDialog.companyUid')}
            value={companyIdentifier}
            onChange={handleCompanyIdentifierChange}
            placeholder={t('companySelector.joinDialog.companyUidPlaceholder')}
            disabled={isSearching || isJoining}
          />

          {!searchResult && (
            <PrimaryButton
              onClick={handleSearch}
              disabled={!countryId || !companyIdentifier.trim() || isSearching}
              className="w-full"
            >
              {isSearching
                ? t('companySelector.joinDialog.searching')
                : t('companySelector.joinDialog.search')}
            </PrimaryButton>
          )}

          {searchResult && (
            <div className="border border-border rounded-lg p-4 bg-background">
              <p className="text-sm text-muted mb-1">
                {t('companySelector.joinDialog.companyFound')}
              </p>
              <p className="font-semibold text-onsurface">{searchResult.companyName}</p>
            </div>
          )}
        </div>
      </DialogBoddy>
      <DialogFooter>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isJoining}
            className="px-4 py-2 text-sm font-medium text-muted hover:text-onsurface transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          {searchResult && (
            <PrimaryButton onClick={handleJoin} disabled={isJoining}>
              {isJoining
                ? t('companySelector.joinDialog.sending')
                : t('companySelector.joinDialog.sendJoinRequest')}
            </PrimaryButton>
          )}
        </div>
      </DialogFooter>
    </Dialog>
  );
}
